import { useEffect, useState } from 'react';
import { X, RotateCcw, ShieldCheck } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchFinances, initialiserCampagneTresorerie } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';

const field =
  'mt-1 h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';

const debutCampagne = () => {
  const d = new Date();
  const an = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
  return `${an}-09-01`;
};

// Reprise des à-nouveaux : solde réel de chaque compte à la date de début de campagne.
export const InitialisationCampagne = ({ soldeReleveInitial, compteReleveId, onClose, onDone }) => {
  const { user } = useAuth();
  const [date, setDate] = useState(debutCampagne());
  const [soldes, setSoldes] = useState([]);

  useEffect(() => {
    fetchFinances().then((f) =>
      setSoldes(
        f.soldes.map((c) => ({
          id: c.id,
          nom: c.nom,
          type: c.type,
          theorique: c.solde,
          solde:
            c.id === compteReleveId && soldeReleveInitial !== null && soldeReleveInitial !== undefined && Number.isFinite(Number(soldeReleveInitial))
              ? String(soldeReleveInitial)
              : String(c.solde),
        }))
      )
    );
  }, [compteReleveId, soldeReleveInitial]);

  const valider = async () => {
    const { error, ignorees, comptes } = await initialiserCampagneTresorerie({
      date,
      soldes: soldes.map((s) => ({ id: s.id, solde: Number(s.solde) || 0 })),
      utilisateur: user?.name,
    });
    if (error) return toast.error(error);
    toast.success(
      `Campagne initialisée au ${date} — ${comptes} compte(s) recalés, ${ignorees} écriture(s) passées en « Exercice antérieur / Ignoré »`,
      { duration: 9000 }
    );
    onDone?.(date);
    onClose();
  };

  return (
    <div data-testid="modale-initialisation" className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4">
      <div className="w-full max-w-3xl border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black text-slate-900">
              <RotateCcw size={18} className="text-sky-700" /> Initialiser la campagne — reprise des à-nouveaux
            </h2>
            <p className="text-xs text-slate-500">
              Le solde d’ouverture de chaque compte est forcé au solde réel constaté à cette date, et toutes les
              écritures antérieures passent en « Exercice antérieur / Ignoré ». L’écart théorique / banque repart à 0,000.
            </p>
          </div>
          <button type="button" data-testid="init-fermer" onClick={onClose} className="border border-slate-300 p-2 text-slate-600 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4">
          <label className="block max-w-xs">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Date de début de campagne</span>
            <input data-testid="init-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} />
          </label>

          <table className="mt-4 w-full border-collapse">
            <thead className="bg-slate-50">
              <tr>
                {['Compte', 'Solde théorique ERP', 'Solde réel au début de campagne'].map((c) => (
                  <th key={c} className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {soldes.map((s, i) => (
                <tr key={s.id} data-testid={`init-compte-${s.id}`}>
                  <td className="px-3 py-2 text-sm text-slate-800">
                    {s.nom}
                    <span className="block font-data text-[10px] uppercase text-slate-400">{s.type}</span>
                  </td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-600">{fmt(s.theorique, 3)}</td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`init-solde-${s.id}`}
                      type="number"
                      step="0.001"
                      value={s.solde}
                      onChange={(e) => setSoldes(soldes.map((x, j) => (j === i ? { ...x, solde: e.target.value } : x)))}
                      className="h-10 w-40 border border-slate-300 bg-slate-50 px-2 text-right font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="mt-3 flex items-start gap-2 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
            Opération journalisée dans le journal d’audit et réversible à tout moment (bouton « Annuler l’initialisation »).
            Les montants négatifs sont acceptés (découvert bancaire).
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" data-testid="init-annuler" onClick={onClose} className="h-11 flex-1 border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-100">
              Annuler
            </button>
            <button type="button" data-testid="init-valider" onClick={valider} className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold text-white hover:bg-slate-700">
              Initialiser la campagne au {date}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
